/**
 * SuperadminUserDrawer (SF-3736).
 *
 * Drill-in detail for a single super-admin (SF internal staff) row on the
 * super-admin usage view. Shows the user's headline totals for the selected
 * period plus their usage trend (daily 30-day / monthly 12-month), fed by the
 * per-user series endpoint (SF-3731 GET /user/{userId}/series).
 *
 * ⚠️ Super-admins have NO company. This drawer therefore never references a
 * company name/id and renders gracefully for company-less users — it is a
 * deliberately company-agnostic alternative to the company-scoped user drawer
 * (SF-3735), so the two stay disjoint.
 */
import { useMemo, useState } from 'react';
import { Col, Drawer, Radio, Row, Space, Tag, Typography } from 'antd';
import { DollarOutlined, MessageOutlined } from '@ant-design/icons';

import { KpiTile } from '../../../components/kpi/KpiTile';
import { TrendChart, type TrendPoint } from '../../../components/charts/TrendChart';
import { formatCurrency, formatNumber } from '../../../utils/format';
import {
  dailyTrendRange,
  monthlyTrendRange,
  type Granularity,
} from '../../../utils/date';
import { useUserSeries, type SuperadminUserRow } from '../api/evaUsageClient';

const { Text } = Typography;

export interface SuperadminUserDrawerProps {
  /** The selected super-admin row, or null when the drawer is closed. */
  user: SuperadminUserRow | null;
  /** Period label ("YYYY-MM") shown alongside the headline totals. */
  period: string;
  /** Whether the request that fetched `user` is gated (super-admin resolved). */
  enabled: boolean;
  /** Close handler. */
  onClose: () => void;
}

/** Per-super-admin usage detail drawer (company-agnostic). */
export const SuperadminUserDrawer: React.FC<SuperadminUserDrawerProps> = ({
  user,
  period,
  enabled,
  onClose,
}) => {
  const [granularity, setGranularity] = useState<Granularity>('daily');

  // Derive the chart from/to on every render (like currentPeriod above) rather
  // than memoizing with [granularity] — a granularity-only dep list still
  // freezes the from/to window at first mount, so a tab left open across a UTC
  // day or month boundary keeps requesting the original end date and never picks
  // up new buckets until a remount/reload (matches company-detail / overview).
  const range =
    granularity === 'daily' ? dailyTrendRange() : monthlyTrendRange();

  const userId = user?.userId ?? null;
  const seriesQuery = useUserSeries(
    userId,
    { ...range, granularity },
    { enabled: enabled && Boolean(userId) },
  );
  const series = seriesQuery.data?.data;

  const trendPoints: TrendPoint[] = useMemo(
    () =>
      (series?.series ?? []).map((point) => ({
        bucket: point.bucket,
        value: Number.isFinite(Number(point.costUsd)) ? Number(point.costUsd) : 0,
      })),
    [series],
  );

  const title = user?.userName || user?.userEmail || 'Super-admin';
  const cost = user?.totals?.costUsd ?? null;
  const messageCount = user?.totals?.messageCount ?? null;

  return (
    <Drawer
      open={user !== null}
      onClose={onClose}
      width={560}
      title={
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {user?.userEmail && user.userEmail !== title ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {user.userEmail}
            </Text>
          ) : null}
        </Space>
      }
      destroyOnHidden
    >
      {user && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space>
            <Tag color="purple">SF internal</Tag>
            <Tag>{period}</Tag>
          </Space>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <KpiTile
                title="Spend"
                value={formatCurrency(cost)}
                icon={<DollarOutlined />}
              />
            </Col>
            <Col xs={24} sm={12}>
              <KpiTile
                title="Messages"
                value={formatNumber(messageCount)}
                icon={<MessageOutlined />}
              />
            </Col>
          </Row>

          <TrendChart
            title="Usage Trend"
            data={trendPoints}
            granularity={granularity}
            loading={seriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
            extra={
              <Radio.Group
                value={granularity}
                onChange={(e) => setGranularity(e.target.value as Granularity)}
                optionType="button"
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
            }
          />
        </Space>
      )}
    </Drawer>
  );
};
